import { CardImg, CardBody, CardTitle, Button } from "reactstrap"
import React, { useState, useEffect } from "react"
import { usePlatforms } from '../contexts/PlatformsContext'

const formatCurrency = (value, currency) => {
  if (value == null || isNaN(value)) return null
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(value)
  } catch {
    return `${currency || ""} ${Number(value).toFixed(2)}`.trim()
  }
}

// Extracts current/original price + discount % from the tracker price object
const getPriceInfo = (price) => {
  if (!price || typeof price !== "object") return null
  const currency = price.currency || price.og_price?.currency || "USD"
  const current = price.amount != null ? Number(price.amount) : null
  // Meta: og_price.offset_amount (cents string); Steam: initial_amount (dollars)
  const ogCents = price.og_price?.offset_amount != null ? Number(price.og_price.offset_amount) : null
  let original = ogCents != null && !isNaN(ogCents) ? ogCents / 100 : null
  if (original == null && price.initial_amount != null && !isNaN(Number(price.initial_amount))) {
    original = Number(price.initial_amount)
  }
  if (current == null && original == null) return null
  const hasDiscount = original != null && current != null && original > current
  const apiPct = Number(price.discount_percent)
  return {
    current: current === 0 ? "Free" : formatCurrency(current, currency),
    original: hasDiscount ? (price.og_price?.formatted || formatCurrency(original, currency)) : null,
    discountPct: hasDiscount
      ? (apiPct > 0 ? Math.round(apiPct) : Math.round((1 - current / original) * 100))
      : null,
  }
}

const TrackedGame = ({ trackedApp, showTrack = true, platformId, showPlatformName = false, showPrice = false }) => {
  const priceInfo = showPrice ? getPriceInfo(trackedApp?.price) : null
  const { getPlatformIcon, getPlatformDisplayName } = usePlatforms()
  const effectivePlatformId = platformId || trackedApp?.platformId
  const platformIconUrl = effectivePlatformId ? getPlatformIcon(effectivePlatformId) : null
  const platformDisplayName = effectivePlatformId
    ? (getPlatformDisplayName(effectivePlatformId) || trackedApp?.platform)
    : trackedApp?.platform
  const [isHovered, setIsHovered] = useState(false)
  const [isHoveredViewButton, setIsHoveredViewButton] = useState(false)
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  )

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Responsive button style function
  const getResponsiveButtonStyle = () => {
    const baseStyle = {
      backgroundColor: isHoveredViewButton ? "#ff4f7a" : "#F6255A",
      borderRadius: "8px",
      border: "none",
      color: "#fff",
      fontWeight: "500",
      cursor: "pointer",
      whiteSpace: "nowrap",
      transition: "background-color 0.2s ease",
    }

    // Mobile view (below 768px)
    if (windowWidth < 768) {
      return {
        ...baseStyle,
        minWidth: "36px",
        fontSize: "9px",
        padding: "2px 5px",
      }
    }

    // Desktop view
    return {
      ...baseStyle,
      minWidth: "44px",
      fontSize: "10px",
      padding: "3px 6px",
    }
  }

  const viewButtonStyle = getResponsiveButtonStyle()

  // COMPACT CARD STYLE
  const cardStyle = {
    padding: "10px",
    borderRadius: "10px",
    background: isHovered ? "#1f202b" : "#171821",
    boxShadow: "0px 2px 4px #00000033",
    transition: "background 0.3s ease",
    width: "100%",
  }

  return (
    <div
      className="w-100 track_game_boxes"
      style={cardStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="d-flex flex-row w-100 align-items-center">
        {/* Image */}
        <CardImg
          src={trackedApp?.image_url || "https://via.placeholder.com/100"}
          alt="Game Image"
          className="me-2"
          style={{
            width: "56px",
            height: "56px",
            minWidth: "56px",
            minHeight: "56px",
            objectFit: "cover",
            borderRadius: "6px",
            flexShrink: 0,
          }}
        />

        {/* Text Section */}
        <CardBody
          className="p-0 me-2"
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <CardTitle tag="h5" className="mb-0">
            <div
              style={{
                fontFamily: "Rubik",
                fontWeight: 600,
                fontSize: "14px",
                lineHeight: "1.2",
                color: "#ffffff",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {trackedApp.name}
            </div>

            <div
              style={{
                fontFamily: "Rubik",
                fontWeight: 400,
                fontSize: "11px",
                color: "#ffffffcc",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                marginTop: "2px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {trackedApp?.category || ""}

              {platformIconUrl && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <img
                    src={platformIconUrl}
                    alt={platformDisplayName || effectivePlatformId}
                    style={{
                      width: "20px",
                      height: "20px",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {showPlatformName && platformDisplayName && <span>{platformDisplayName}</span>}
                </span>
              )}
            </div>
          </CardTitle>
        </CardBody>

        {/* Price (optional) */}
        {priceInfo && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              flexShrink: 0,
              fontFamily: "Rubik",
              gap: "2px",
            }}
          >
            {priceInfo.discountPct != null && (
              <span
                style={{
                  background: "#1f8f4e",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: 600,
                  padding: "1px 6px",
                  borderRadius: "4px",
                }}
              >
                -{priceInfo.discountPct}%
              </span>
            )}
            <span style={{ color: "#fff", fontSize: "13px", fontWeight: 600 }}>{priceInfo.current}</span>
            {priceInfo.original && (
              <span style={{ color: "#ffffff80", fontSize: "11px", textDecoration: "line-through" }}>
                {priceInfo.original}
              </span>
            )}
          </div>
        )}

        {/* Button (optional) */}
        {showTrack && (
          <Button
            className="ms-auto"
            style={viewButtonStyle}
            onMouseEnter={() => setIsHoveredViewButton(true)}
            onMouseLeave={() => setIsHoveredViewButton(false)}
          >
            Track
          </Button>
        )}
      </div>
    </div>
  )
}

export default TrackedGame
